// SalesDashboard.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import {
    Autocomplete,
    Card,
    TextField,
    Tooltip as MuiTooltip,
    Chip,
    Button,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CrudService from "services/cruds-service";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import FlightIcon from "@mui/icons-material/Flight";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import DataTable from "../../../../../../examples/Tables/DataTable";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Checkbox from "@mui/material/Checkbox";

// ✅ Chart.js register
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PointElement,
    LineElement,
    Tooltip as ChartTooltip,
    Legend,
} from "chart.js";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import PaymentsOutlined from "@mui/icons-material/PaymentsOutlined";
import {
    CrmFilterBar,
    CrmActionButton,
    CrmCard,
    CrmSectionHeader,
    CrmSegmented,
    CrmKpiTile,
    CRM_COLORS,
    crmCardSx,
} from "../shared/CrmUi";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PointElement,
    LineElement,
    ChartTooltip,
    Legend
);

// -------------------- FX --------------------
const fetchExchangeRates = async () => {
    try {
        const res = await fetch("https://api.exchangerate-api.com/v4/latest/USD");
        return await res.json();
    } catch (error) {
        console.error("Exchange Rate API Error:", error);
        return null;
    }
};

function convertCurrency(amount, fromCurrency, toCurrency, rates) {
    if (!rates) return amount;
    const fromRate = rates.rates?.[fromCurrency];
    const toRate = rates.rates?.[toCurrency];
    if (!fromRate || !toRate) return amount;
    return (amount / fromRate) * toRate;
}

const safeStr = (v) => (v == null ? "" : String(v).trim());
const shorten = (s, n = 14) => {
    const t = safeStr(s);
    if (!t) return "";
    return t.length > n ? `${t.slice(0, n)}…` : t;
};

const makeColors = (n) => {
    const arr = [];
    for (let i = 0; i < n; i += 1) {
        const hue = Math.round((360 * i) / Math.max(1, n));
        arr.push(`hsl(${hue}, 65%, 55%)`);
    }
    return arr;
};

const buildTopNWithOthers = (items, getKey, topN = 14, otherLabel = "Others") => {
    const map = {};
    items.forEach((it) => {
        const k = safeStr(getKey(it)) || "NoName";
        map[k] = (map[k] || 0) + 1;
    });

    const sorted = Object.entries(map)
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count);

    const top = sorted.slice(0, topN);
    const rest = sorted.slice(topN);
    const othersCount = rest.reduce((s, x) => s + x.count, 0);

    const fullLabels = top.map((x) => x.label);
    const counts = top.map((x) => x.count);

    if (othersCount > 0) {
        fullLabels.push(otherLabel);
        counts.push(othersCount);
    }

    const shortLabels = fullLabels.map((l) => shorten(l, 14));
    return { fullLabels, shortLabels, counts };
};

const fmt2 = (n) =>
    Number(n || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const chartClickLabel = (evt, elements, chart) => {
    if (!elements?.length) return null;
    const idx = elements[0]?.index;
    const label = chart?.data?.labels?.[idx];
    return label != null ? String(label) : null;
};

// ✅ Doughnut options (tooltip counts + click)
const doughnutOptionsFactory = (onClickHandler, labelResolver) => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
        legend: {
            position: "bottom",
            labels: { boxWidth: 12, padding: 10, font: { size: 12 } },
        },
        tooltip: {
            callbacks: {
                title: (items) => {
                    const idx = items?.[0]?.dataIndex ?? 0;
                    const full = typeof labelResolver === "function" ? labelResolver(idx) : null;
                    return full || items?.[0]?.label || "";
                },
                label: (ctx) => ` ${ctx.raw ?? 0}`,
            },
        },
    },
    onClick: (evt, elements, chart) => {
        const label = chartClickLabel(evt, elements, chart);
        if (label && onClickHandler) onClickHandler(label, chart);
    },
});

function SalesDashboard() {
    const [opportunities, setOpportunities] = useState([]);
    const [filterMode, setFilterMode] = useState("ALL"); // ALL | SINGLE | MULTI
    const [selectedTypes, setSelectedTypes] = useState([]); // transport type cards

    const [analysisByType, setAnalysisByType] = useState({});
    const [selectedCurrency, setSelectedCurrency] = useState("USD");
    const [exchangeRates, setExchangeRates] = useState(null);

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const ALLOWED_STATUS_LABELS = ["Prospect", "Needs Assessment", "Target"];
    const [selectedStatuses, setSelectedStatuses] = useState(ALLOWED_STATUS_LABELS);

    const [loading, setLoading] = useState(true);

    // ✅ global click filters
    const [chartFilters, setChartFilters] = useState([]); // [{type,value}]
    const navigate = useNavigate();

    const colors = {
        Road: "#494848",
        Ocean: "#061a9e",
        Air: "#6186f8",
        Intermodal: "#098133",
        ProjectCargo: "#ff7400",
    };

    const typeStyles = {
        Road: { label: "Road", color: "#494848", icon: <DirectionsCarIcon /> },
        Ocean: { label: "Ocean", color: "#061a9e", icon: <DirectionsBoatIcon /> },
        Air: { label: "Air", color: "#6186f8", icon: <FlightIcon /> },
        Intermodal: { label: "Intermodal", color: "#098133", icon: <LocalShippingIcon /> },
        ProjectCargo: { label: "Project Cargo", color: "#ff7400", icon: <BusinessCenterIcon /> },
    };

    const clearTypeFilter = () => {
        setSelectedTypes([]);
        setFilterMode("ALL");
    };

    const setSingleType = (type) => {
        setSelectedTypes([type]);
        setFilterMode("SINGLE");
    };

    const toggleMultiType = (type) => {
        setFilterMode("MULTI");
        setSelectedTypes((prev) => {
            if (prev.includes(type)) return prev.filter((t) => t !== type);
            return [...prev, type];
        });
    };

    useEffect(() => {
        if (filterMode === "ALL" && selectedTypes.length > 0) setSelectedTypes([]);
        if (filterMode === "SINGLE" && selectedTypes.length > 1) setSelectedTypes([selectedTypes[0]]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterMode]);

    const normalizeStatus = (op) => {
        const raw = op?.status ?? op?.opportunityStatus;
        if (!raw) return "";
        if (typeof raw === "string") return raw.trim();
        if (typeof raw === "object" && raw.label) return String(raw.label).trim();
        return String(raw).trim();
    };

    const normalizeSalesReps = (op) => {
        const reps = Array.isArray(op?.salesRep)
            ? op.salesRep.map((r) => r?.name).filter(Boolean)
            : [op?.salesRep?.name || op?.salesRep].filter(Boolean);

        return reps.map((x) => safeStr(x)).filter(Boolean);
    };

    // --------- chart filter actions ----------
    const addFilter = (type, value) => {
        const t = safeStr(type);
        const v = safeStr(value);
        if (!t || !v) return;

        setChartFilters((prev) => {
            const clean = Array.isArray(prev) ? prev.filter((x) => x?.type && x?.value) : [];
            if (clean.some((x) => x.type === t && x.value === v)) return clean;
            return [...clean, { type: t, value: v }];
        });
    };

    const removeFilter = (f) => {
        if (!f?.type || !f?.value) return;
        setChartFilters((prev) =>
            (Array.isArray(prev) ? prev : []).filter((x) => !(x.type === f.type && x.value === f.value))
        );
    };

    const clearAllChartFilters = () => setChartFilters([]);

    // --- fetch
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [oppRes, rates] = await Promise.all([CrudService.getOpportunities(), fetchExchangeRates()]);
                const opps = oppRes.data.map((item) => ({ id: item.id, ...item.attributes }));
                setOpportunities(opps);
                setExchangeRates(rates);
            } catch (error) {
                console.error("Error fetching opportunities:", error);
            }
            setLoading(false);
        };

        fetchData();
    }, []);

    const applyStatusFilter = (data) => {
        if (!selectedStatuses || selectedStatuses.length === 0) return [];
        const set = new Set(selectedStatuses);
        return data.filter((op) => set.has(normalizeStatus(op)));
    };

    const applyDateRangeFilter = (data) => {
        if (!startDate || !endDate) return data;

        const start = new Date(startDate.startOf("day").toISOString());
        const end = new Date(endDate.endOf("day").toISOString());

        return data.filter((op) => {
            const dt = new Date(op.estimatedCloseDate);
            if (isNaN(dt.getTime())) return false;
            return dt >= start && dt <= end;
        });
    };

    const applyChartFilters = (data) => {
        const filters = Array.isArray(chartFilters) ? chartFilters.filter((x) => x?.type && x?.value) : [];
        if (!filters.length) return data;

        return data.filter((op) => {
            for (const f of filters) {
                if (f.type === "status") {
                    if (normalizeStatus(op) !== f.value) return false;
                }
                if (f.type === "transportType") {
                    if (safeStr(op.selectedTransportType || "UndefinedType") !== safeStr(f.value)) return false;
                }
                if (f.type === "salesRep") {
                    const reps = normalizeSalesReps(op);
                    if (!reps.includes(f.value)) return false;
                }
                if (f.type === "customer") {
                    if (safeStr(op.customer || "NoName") !== safeStr(f.value)) return false;
                }
                if (f.type === "currency") {
                    if (safeStr(op.freightRateCurrency || "Unknown") !== safeStr(f.value)) return false;
                }
                if (f.type === "closeMonth") {
                    const dt = new Date(op.estimatedCloseDate);
                    if (isNaN(dt.getTime())) return false;
                    const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
                    if (key !== f.value) return false;
                }
                if (f.type === "closeWeek") {
                    const dt = new Date(op.estimatedCloseDate);
                    if (isNaN(dt.getTime())) return false;
                    const year = dt.getFullYear();
                    const oneJan = new Date(year, 0, 1);
                    const dayOfYear = Math.floor((dt - oneJan) / 86400000) + 1;
                    const week = Math.ceil(dayOfYear / 7);
                    const key = `${year}-W${String(week).padStart(2, "0")}`;
                    if (key !== f.value) return false;
                }
            }
            return true;
        });
    };

    function buildAnalysisByType(opps, rates, targetCurrency) {
        const data = {};
        opps.forEach((op) => {
            const type = op.selectedTransportType || "UndefinedType";
            if (!data[type]) data[type] = { totalRevenue: 0, count: 0 };

            const revenue = convertCurrency(Number(op.totalRevenue || 0), op.freightRateCurrency, targetCurrency, rates);
            data[type].totalRevenue += revenue;
            data[type].count += 1;
        });
        return data;
    }

    // baseOpps (date + status)
    const baseOpps = useMemo(() => {
        const withStatus = applyStatusFilter(opportunities);
        return applyDateRangeFilter(withStatus);
    }, [opportunities, startDate, endDate, selectedStatuses]);

    // transport type cards filter
    const typeFilteredOpps = useMemo(() => {
        if (filterMode === "ALL" || selectedTypes.length === 0) return baseOpps;
        return baseOpps.filter((op) => selectedTypes.includes(op.selectedTransportType));
    }, [baseOpps, filterMode, selectedTypes]);

    // final pageOpps (type + chart clicks)
    const pageOpps = useMemo(() => applyChartFilters(typeFilteredOpps), [typeFilteredOpps, chartFilters]);

    const selectedLabel = filterMode === "ALL" || selectedTypes.length === 0 ? "All" : selectedTypes.join(", ");

    useEffect(() => {
        setAnalysisByType(buildAnalysisByType(baseOpps, exchangeRates, selectedCurrency));
    }, [baseOpps, exchangeRates, selectedCurrency]);

    const pageAnalysisByType = useMemo(
        () => buildAnalysisByType(pageOpps, exchangeRates, selectedCurrency),
        [pageOpps, exchangeRates, selectedCurrency]
    );

    const pageTotalCount = pageOpps.length;

    const pageTotalRevenue = useMemo(
        () => Object.values(pageAnalysisByType).reduce((acc, item) => acc + (item.totalRevenue || 0), 0),
        [pageAnalysisByType]
    );

    const formatNumber = (num) => Number(num || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

    const showTable = useMemo(() => {
        const hasChart = chartFilters?.length > 0;
        const hasType = !(filterMode === "ALL" || selectedTypes.length === 0);
        return hasChart || hasType;
    }, [chartFilters, filterMode, selectedTypes]);

    // -------------------- DOUGHNUTS --------------------
    const statusDoughnut = useMemo(() => {
        const map = {};
        pageOpps.forEach((op) => {
            const k = normalizeStatus(op) || "Unknown";
            map[k] = (map[k] || 0) + 1;
        });
        const labels = Object.keys(map);
        const data = labels.map((l) => map[l]);
        return { labels, datasets: [{ data, backgroundColor: makeColors(labels.length), borderWidth: 2 }] };
    }, [pageOpps]);

    const transportDoughnut = useMemo(() => {
        const map = {};
        pageOpps.forEach((op) => {
            const k = op.selectedTransportType || "UndefinedType";
            map[k] = (map[k] || 0) + 1;
        });
        const labels = Object.keys(map);
        return {
            labels,
            datasets: [{ data: labels.map((l) => map[l]), backgroundColor: labels.map((t) => colors[t] || "#777"), borderWidth: 2 }],
        };
    }, [pageOpps]);

    const salesRepDoughnut = useMemo(() => {
        const map = {};
        pageOpps.forEach((op) => {
            const reps = normalizeSalesReps(op);
            if (!reps.length) {
                map["Unassigned"] = (map["Unassigned"] || 0) + 1;
                return;
            }
            reps.forEach((n) => {
                const k = safeStr(n) || "Unassigned";
                map[k] = (map[k] || 0) + 1;
            });
        });

        const sorted = Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);

        const labels = sorted.map((x) => x.label);
        return { labels, datasets: [{ data: sorted.map((x) => x.count), backgroundColor: makeColors(labels.length), borderWidth: 2 }] };
    }, [pageOpps]);

    const currencyDoughnut = useMemo(() => {
        const map = {};
        pageOpps.forEach((op) => {
            const k = safeStr(op.freightRateCurrency) || "Unknown";
            map[k] = (map[k] || 0) + 1;
        });
        const labels = Object.keys(map);
        return { labels, datasets: [{ data: labels.map((l) => map[l]), backgroundColor: makeColors(labels.length), borderWidth: 2 }] };
    }, [pageOpps]);

    // -------------------- BAR CHARTS --------------------
    const salesRepBar = useMemo(() => {
        const map = {};
        pageOpps.forEach((op) => {
            const reps = normalizeSalesReps(op);
            if (!reps.length) {
                map["Unassigned"] = (map["Unassigned"] || 0) + 1;
                return;
            }
            reps.forEach((n) => {
                const k = safeStr(n) || "Unassigned";
                map[k] = (map[k] || 0) + 1;
            });
        });

        const sorted = Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 14);

        return {
            labels: sorted.map((x) => x.label),
            datasets: [{ label: "Opportunities", data: sorted.map((x) => x.count), borderRadius: 12, maxBarThickness: 28 }],
        };
    }, [pageOpps]);

    const salesRepBarOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: "y",
            plugins: { legend: { display: false } },
            scales: {
                x: { beginAtZero: true, grid: { color: "rgba(0,0,0,0.06)" }, ticks: { stepSize: 1 } },
                y: {
                    grid: { display: false },
                    ticks: {
                        callback: function (value) {
                            const label = this.getLabelForValue(value);
                            return String(label).length > 22 ? String(label).slice(0, 22) + "…" : label;
                        },
                    },
                },
            },
            onClick: (evt, elements, chart) => {
                const label = chartClickLabel(evt, elements, chart);
                if (label) addFilter("salesRep", label);
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const customerColumn = useMemo(() => {
        const { fullLabels, shortLabels, counts } = buildTopNWithOthers(pageOpps, (op) => op.customer, 14, "Others");
        return {
            labels: shortLabels,
            fullLabels,
            datasets: [{ label: "Opportunities", data: counts, borderRadius: 12, maxBarThickness: 46 }],
        };
    }, [pageOpps]);

    const customerColumnOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        title: (items) => {
                            const idx = items?.[0]?.dataIndex ?? 0;
                            return customerColumn.fullLabels?.[idx] || "";
                        },
                        label: (ctx) => ` ${ctx.raw ?? 0} opportunities`,
                    },
                },
            },
            scales: {
                x: { grid: { display: false }, ticks: { maxRotation: 35, minRotation: 35 } },
                y: { beginAtZero: true, grid: { color: "rgba(0,0,0,0.06)" }, ticks: { stepSize: 1 } },
            },
            onClick: (evt, elements) => {
                if (!elements?.length) return;
                const idx = elements[0]?.index ?? 0;
                const full = customerColumn.fullLabels?.[idx];
                if (full) addFilter("customer", full);
            },
        }),
        [customerColumn]
    );

    // -------------------- WEEK / MONTH --------------------
    const byMonth = useMemo(() => {
        const map = {};
        pageOpps.forEach((op) => {
            const dt = new Date(op.estimatedCloseDate);
            if (isNaN(dt.getTime())) return;
            const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
            map[key] = (map[key] || 0) + 1;
        });
        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [pageOpps]);

    const byWeek = useMemo(() => {
        const map = {};
        pageOpps.forEach((op) => {
            const dt = new Date(op.estimatedCloseDate);
            if (isNaN(dt.getTime())) return;
            const year = dt.getFullYear();
            const oneJan = new Date(year, 0, 1);
            const dayOfYear = Math.floor((dt - oneJan) / 86400000) + 1;
            const week = Math.ceil(dayOfYear / 7);
            const key = `${year}-W${String(week).padStart(2, "0")}`;
            map[key] = (map[key] || 0) + 1;
        });
        return Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [pageOpps]);

    const weekLineData = useMemo(
        () => ({
            labels: byWeek.map((x) => x.label),
            datasets: [{ label: "Opportunities", data: byWeek.map((x) => x.count), tension: 0.35, borderWidth: 3, fill: false }],
        }),
        [byWeek]
    );

    const monthLineData = useMemo(
        () => ({
            labels: byMonth.map((x) => x.label),
            datasets: [{ label: "Opportunities", data: byMonth.map((x) => x.count), tension: 0.35, borderWidth: 3, fill: false }],
        }),
        [byMonth]
    );

    const lineOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: {
                x: { grid: { display: false }, ticks: { autoSkip: true, maxTicksLimit: 10, maxRotation: 45, minRotation: 45 } },
                y: { beginAtZero: true, grid: { color: "rgba(0,0,0,0.06)" }, ticks: { stepSize: 1 } },
            },
        }),
        []
    );

    // -------------------- TOTAL REVENUE --------------------
    function TotalRevenueLineChart({ analysisByType, selectedCurrency }) {
        const transportTypes = Object.keys(analysisByType);
        const data = {
            labels: transportTypes,
            datasets: [
                {
                    label: "Total Revenue",
                    data: transportTypes.map((type) => analysisByType[type].totalRevenue || 0),
                    borderWidth: 3,
                    tension: 0.35,
                    fill: false,
                },
            ],
        };

        const options = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (context) => `${fmt2(context.raw)} ${selectedCurrency}`,
                    },
                },
            },
            scales: {
                x: { grid: { display: false } },
                y: {
                    beginAtZero: true,
                    grid: { color: "rgba(0,0,0,0.06)" },
                    title: { display: true, text: `Total Revenue (${selectedCurrency})` },
                    ticks: {
                        callback: (v) => fmt2(v),
                    },
                },
            },
        };

        return <Line data={data} options={options} />;
    }

    function TotalRevenueDoughnutChart({ analysisByType, selectedCurrency }) {
        const transportTypes = Object.keys(analysisByType);

        const data = useMemo(() => {
            return {
                labels: transportTypes,
                datasets: [
                    {
                        data: transportTypes.map((type) => Number(analysisByType[type]?.totalRevenue || 0)),
                        backgroundColor: transportTypes.map((type) => typeStyles[type]?.color || "#777"),
                        borderWidth: 2,
                    },
                ],
            };
        }, [transportTypes, analysisByType]);

        // ✅ plugin'i stabilize et + center area temizle + fmt2 ile yaz
        const centerTextPlugin = useMemo(
            () => ({
                id: "centerTextPluginRevenue",
                afterDraw(chart) {
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return;

                    // ✅ donut iç yarıçapını yakala (hole radius)
                    const meta = chart.getDatasetMeta(0);
                    const arc = meta?.data?.[0];
                    const innerRadius = arc?.innerRadius || 0;

                    const x = (chartArea.left + chartArea.right) / 2;
                    const y = (chartArea.top + chartArea.bottom) / 2;

                    const values = chart.data?.datasets?.[0]?.data || [];
                    const total = values.reduce((sum, v) => sum + Number(v || 0), 0);

                    const title = "Total";
                    const value = fmt2(total);

                    ctx.save();

                    // ✅ 1) İç boşluğu komple temizle (kim yazdıysa kapat)
                    ctx.beginPath();
                    ctx.arc(x, y, Math.max(0, innerRadius - 2), 0, Math.PI * 2);
                    ctx.fillStyle = "rgba(255,255,255,1)";
                    ctx.fill();

                    // ✅ 2) Senin rounded rect + yazı
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";

                    ctx.font = "600 12px Arial";
                    const titleW = ctx.measureText(title).width;

                    ctx.font = "700 16px Arial";
                    const valueW = ctx.measureText(value).width;

                    const w = Math.max(titleW, valueW) + 28;
                    const h = 54;
                    const r = 12;

                    const left = x - w / 2;
                    const top = y - h / 2;

                    ctx.fillStyle = "rgba(255,255,255,0.92)";
                    ctx.beginPath();
                    ctx.moveTo(left + r, top);
                    ctx.arcTo(left + w, top, left + w, top + h, r);
                    ctx.arcTo(left + w, top + h, left, top + h, r);
                    ctx.arcTo(left, top + h, left, top, r);
                    ctx.arcTo(left, top, left + w, top, r);
                    ctx.closePath();
                    ctx.fill();

                    ctx.font = "600 12px Arial";
                    ctx.fillStyle = "#777";
                    ctx.fillText(title, x, y - 12);

                    ctx.font = "700 16px Arial";
                    ctx.fillStyle = "#111";
                    ctx.fillText(value, x, y + 10);

                    ctx.restore();
                },
            }),
            []
        );


        const options = useMemo(() => ({
            responsive: true,
            maintainAspectRatio: false,
            cutout: "68%",
            plugins: {
                legend: { position: "bottom" },
                tooltip: {
                    position: "nearest",
                    yAlign: "top",          // ✅ yukarıda dursun
                    xAlign: "center",
                    padding: 10,
                    caretPadding: 32,       // ✅ ok ile kutu arası mesafe
                    callbacks: {
                        label: (context) => `${context.label}: ${fmt2(context.raw)} ${selectedCurrency}`,
                    },
                },

                datalabels: { display: false },
            },
        }), [selectedCurrency]);




        return (
            <Doughnut
                data={data}
                options={options}
                plugins={[
                    // ✅ datalabels’ı bu chart’ta komple devre dışı bırak
                    { id: "datalabels", beforeInit: () => {} },
                    centerTextPlugin
                ]}
            />
        );
    }

    function formatDateDDMMYYYY(dateString) {
        if (!dateString) return "";
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return dateString;
        return d.toLocaleDateString("en-GB");
    }

    // -------------------- TABLE --------------------
    const dataTableColumns = [
        { Header: "Customer", accessor: "customer" },
        { Header: "Opportunity Name", accessor: "opportunityName" },
        { Header: "Estimated Close", accessor: "estimatedCloseDate" },
        { Header: "Transport Type", accessor: "selectedTransportType" },
        { Header: "Total Revenue", accessor: "totalRevenue" },
        { Header: "Sales Rep", accessor: "salesRep" },
        { Header: "Status", accessor: "status" },
        { Header: "Currency", accessor: "currency" },
    ];

    const dataTableRows = pageOpps.map((op) => {
        const revenue = convertCurrency(Number(op.totalRevenue || 0), op.freightRateCurrency, selectedCurrency, exchangeRates);

        return {
            customer: (
                <MuiTooltip title={op.customer || ""} placement="top" arrow>
                    <span>{op.customer?.length > 40 ? `${op.customer.slice(0, 37)}...` : op.customer || ""}</span>
                </MuiTooltip>
            ),
            opportunityName: (
                <span
                    style={{ color: "#ff7400", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => navigate(`/opportunity/create-opportunity/details?id=${op.id}`)}
                >
          {op.opportunityName || "-"}
        </span>
            ),
            estimatedCloseDate: formatDateDDMMYYYY(op.estimatedCloseDate),
            selectedTransportType: op.selectedTransportType || "-",
            totalRevenue: `${fmt2(revenue)} ${selectedCurrency}`,
            salesRep: Array.isArray(op.salesRep) ? op.salesRep.map((rep) => rep.name).join(", ") : op.salesRep?.name || op.salesRep || "-",
            status: normalizeStatus(op) || "-",
            currency: safeStr(op.freightRateCurrency) || "-",
        };
    });

    const metrics = [{ key: "count", title: "Number of Opportunities" }];

    const isClearEnabled =
        (startDate && endDate) ||
        selectedStatuses?.length !== ALLOWED_STATUS_LABELS.length ||
        !ALLOWED_STATUS_LABELS.every((s) => selectedStatuses?.includes(s));

    // -------------------- UI --------------------
    return (
        <MDBox pb={3}>
            {/* ✅ Active chart filters chips */}
            {chartFilters?.length > 0 && (
                <Box mb={2} display="flex" flexWrap="wrap" gap={1} alignItems="center">
                    {chartFilters.map((f, idx) => (
                        <Chip
                            key={`${f.type}-${f.value}-${idx}`}
                            label={`${f.type}: ${f.value}`}
                            onDelete={() => removeFilter(f)}
                            size="small"
                            sx={{ borderRadius: 2, fontWeight: 800 }}
                        />
                    ))}
                    <Button size="small" onClick={clearAllChartFilters} sx={{ textTransform: "none", fontWeight: 700 }}>
                        Clear All
                    </Button>
                </Box>
            )}

            {/* Filtreler */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <CrmFilterBar sx={{ mb: 2.5 }}>
                    <TextField
                        select
                        label="Select Currency"
                        value={selectedCurrency}
                        onChange={(e) => setSelectedCurrency(e.target.value)}
                        SelectProps={{ native: true }}
                        sx={{ minWidth: 160, "& .MuiInputBase-root": { height: "40px" } }}
                        size="small"
                        variant="outlined"
                    >
                        {["USD", "TRY", "EUR", "GBP"].map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </TextField>

                    <DatePicker
                        label="Start Date"
                        format="DD.MM.YYYY"
                        value={startDate}
                        onChange={(newValue) => setStartDate(newValue)}
                        slotProps={{
                            textField: { variant: "outlined", size: "small", sx: { minWidth: 160, "& .MuiInputBase-root": { height: "40px" } } },
                        }}
                    />

                    <DatePicker
                        label="End Date"
                        format="DD.MM.YYYY"
                        value={endDate}
                        onChange={(newValue) => setEndDate(newValue)}
                        slotProps={{
                            textField: { variant: "outlined", size: "small", sx: { minWidth: 160, "& .MuiInputBase-root": { height: "40px" } } },
                        }}
                    />

                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={ALLOWED_STATUS_LABELS}
                        value={selectedStatuses}
                        onChange={(_, newValue) => setSelectedStatuses(newValue)}
                        renderOption={({ key: optionKey, ...props }, option, { selected }) => (
                            <li key={optionKey} {...props}>
                                <Checkbox checked={selected} sx={{ mr: 1 }} />
                                {option}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Status"
                                size="small"
                                variant="outlined"
                                sx={{ minWidth: 260, "& .MuiInputBase-root": { minHeight: "55px" } }}
                            />
                        )}
                    />
                    <CrmActionButton
                        variant="secondary"
                        onClick={() => {
                            setStartDate(null);
                            setEndDate(null);
                            setSelectedStatuses(ALLOWED_STATUS_LABELS);
                            clearAllChartFilters();
                            clearTypeFilter();
                        }}
                        disabled={!isClearEnabled && chartFilters.length === 0 && (filterMode === "ALL" || selectedTypes.length === 0)}
                    >
                        Clear
                    </CrmActionButton>
                </CrmFilterBar>
            </LocalizationProvider>

            {/* (B) Metrik Kartları: taşıma türüne göre fırsat sayısı; karta tıklamak tüm grafikleri filtreler */}
            {metrics.map((metric) => (
                <CrmCard key={metric.key} sx={{ mb: 2.5, height: "auto" }}>
                    <MDBox p={2.5}>
                        <CrmSectionHeader
                            title={metric.title}
                            subtitle="Click a transport type to filter all charts"
                            action={
                                <CrmSegmented
                                    options={[
                                        { value: "ALL", label: "All" },
                                        { value: "SINGLE", label: "Single" },
                                        { value: "MULTI", label: "Multi" },
                                    ]}
                                    value={filterMode}
                                    onChange={(mode) => (mode === "ALL" ? clearTypeFilter() : setFilterMode(mode))}
                                />
                            }
                        />
                        <MDBox
                            display="grid"
                            gridTemplateColumns={{ xs: "repeat(2, minmax(0, 1fr))", md: "repeat(5, minmax(0, 1fr))" }}
                            gap={1.5}
                        >
                            {Object.keys(analysisByType).map((type) => {
                                const styleInfo = typeStyles[type] || { label: type, color: "#667085", icon: null };
                                const value = analysisByType[type]?.[metric.key] || 0;
                                const isSelected = filterMode !== "ALL" && selectedTypes.includes(type);
                                return (
                                    <MDBox
                                        key={type}
                                        component="button"
                                        type="button"
                                        onClick={() => {
                                            if (filterMode === "MULTI") toggleMultiType(type);
                                            else setSingleType(type);
                                        }}
                                        sx={{
                                            ...crmCardSx,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                            p: 1.75,
                                            cursor: "pointer",
                                            fontFamily: "inherit",
                                            textAlign: "left",
                                            borderColor: isSelected ? CRM_COLORS.navy : CRM_COLORS.cardBorder,
                                            boxShadow: isSelected ? `inset 0 0 0 1px ${CRM_COLORS.navy}` : "none",
                                            "&:hover": { borderColor: styleInfo.color },
                                        }}
                                    >
                                        <MDBox
                                            sx={{
                                                width: 38,
                                                height: 38,
                                                borderRadius: "8px",
                                                display: "grid",
                                                placeItems: "center",
                                                flexShrink: 0,
                                                color: styleInfo.color,
                                                background: `${styleInfo.color}1A`,
                                                "& svg": { fontSize: "20px !important" },
                                            }}
                                        >
                                            {styleInfo.icon}
                                        </MDBox>
                                        <MDBox minWidth={0}>
                                            <MDTypography sx={{ fontSize: 12.5, fontWeight: 600, color: CRM_COLORS.subtext }}>
                                                {styleInfo.label}
                                            </MDTypography>
                                            <MDTypography sx={{ fontSize: 22, fontWeight: 700, color: CRM_COLORS.text, lineHeight: 1.15 }}>
                                                {value}
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                );
                            })}
                        </MDBox>
                    </MDBox>
                </CrmCard>
            ))}

            {/* Totals */}
            <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }} gap={2} mb={1}>
                <CrmKpiTile
                    title="All Transport"
                    value={formatNumber(pageTotalCount)}
                    subtitle="Opportunities in the current filter"
                    icon={<Inventory2Outlined />}
                    tone="navy"
                />
                <CrmKpiTile
                    title="Total Revenue"
                    value={`${fmt2(pageTotalRevenue)} ${selectedCurrency}`}
                    subtitle="Converted to the selected currency"
                    icon={<PaymentsOutlined />}
                    tone="green"
                />
            </MDBox>

            {/* ================= CHARTS: mantıklı sıralama + boş kutu yok ================= */}

            {/* 1) Doughnuts (4'lü grid) */}
            <MDBox mt={4}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                        <Card sx={{ p: 2.5, borderRadius: "10px", boxShadow: "none", height: 360 }}>
                            <MDTypography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                Opportunities by Status
                            </MDTypography>
                            <Box sx={{ height: 290 }}>
                                <Doughnut data={statusDoughnut} options={doughnutOptionsFactory((label) => addFilter("status", label))} />
                            </Box>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Card sx={{ p: 2.5, borderRadius: "10px", boxShadow: "none", height: 360 }}>
                            <MDTypography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                Opportunities by Transport Type
                            </MDTypography>
                            <Box sx={{ height: 290 }}>
                                <Doughnut data={transportDoughnut} options={doughnutOptionsFactory((label) => addFilter("transportType", label))} />
                            </Box>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Card sx={{ p: 2.5, borderRadius: "10px", boxShadow: "none", height: 360 }}>
                            <MDTypography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                Opportunities by Sales Rep (Top 10)
                            </MDTypography>
                            <Box sx={{ height: 290 }}>
                                <Doughnut data={salesRepDoughnut} options={doughnutOptionsFactory((label) => addFilter("salesRep", label))} />
                            </Box>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Card sx={{ p: 2.5, borderRadius: "10px", boxShadow: "none", height: 360 }}>
                            <MDTypography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                Opportunities by Currency
                            </MDTypography>
                            <Box sx={{ height: 290 }}>
                                <Doughnut data={currencyDoughnut} options={doughnutOptionsFactory((label) => addFilter("currency", label))} />
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            {/* 2) Bars */}
            <MDBox mt={4}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 2.5, borderRadius: "10px", boxShadow: "none", height: 420 }}>
                            <MDTypography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                Opportunities by Sales Rep (Horizontal)
                            </MDTypography>
                            <Box sx={{ height: 350 }}>
                                <Bar data={salesRepBar} options={salesRepBarOptions} />
                            </Box>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 2.5, borderRadius: "10px", boxShadow: "none", height: 420 }}>
                            <MDTypography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                Opportunities by Customer (Column) — Top 14 + Others
                            </MDTypography>
                            <Box sx={{ height: 350 }}>
                                <Bar data={customerColumn} options={customerColumnOptions} />
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            {/* 3) Trends */}
            <MDBox mt={4}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 2.5, borderRadius: "10px", boxShadow: "none", height: 380 }}>
                            <MDTypography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                Opportunities by Week (Estimated Close)
                            </MDTypography>
                            <Box sx={{ height: 310 }}>
                                <Line
                                    data={weekLineData}
                                    options={{
                                        ...lineOptions,
                                        onClick: (evt, elements, chart) => {
                                            const label = chartClickLabel(evt, elements, chart);
                                            if (label) addFilter("closeWeek", label);
                                        },
                                    }}
                                />
                            </Box>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 2.5, borderRadius: "10px", boxShadow: "none", height: 380 }}>
                            <MDTypography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                Opportunities by Month (Estimated Close)
                            </MDTypography>
                            <Box sx={{ height: 310 }}>
                                <Line
                                    data={monthLineData}
                                    options={{
                                        ...lineOptions,
                                        onClick: (evt, elements, chart) => {
                                            const label = chartClickLabel(evt, elements, chart);
                                            if (label) addFilter("closeMonth", label);
                                        },
                                    }}
                                />
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            {/* 4) Total Revenue */}
            <MDBox mt={4}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 2.5, borderRadius: "10px", boxShadow: "none", height: 420 }}>
                            <MDTypography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                Total Revenue ({selectedLabel} - {selectedCurrency})
                            </MDTypography>
                            <Box sx={{ height: 350 }}>
                                <TotalRevenueLineChart analysisByType={pageAnalysisByType} selectedCurrency={selectedCurrency} />
                            </Box>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 2.5, borderRadius: "10px", boxShadow: "none", height: 420 }}>
                            <MDTypography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                Total Revenue Share ({selectedLabel} - {selectedCurrency})
                            </MDTypography>
                            <Box sx={{ height: 350 }}>
                                <TotalRevenueDoughnutChart analysisByType={pageAnalysisByType} selectedCurrency={selectedCurrency} />
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            {/* ================= TABLE ================= */}
            {showTable && (
                <MDBox mt={5}>
                    <MDTypography variant="h6" mb={2} sx={{ fontWeight: 700 }}>
                        Opportunities (Filtered) — {pageOpps.length} rows
                    </MDTypography>
                    <Box>
                        <DataTable table={{ columns: dataTableColumns, rows: dataTableRows }} canSearch isSorted entriesPerPage={5} showTotalEntries />
                    </Box>
                </MDBox>
            )}

            {loading ? (
                <MDBox
                    sx={{
                        position: "fixed",
                        inset: 0,
                        bgcolor: "rgba(255,255,255,0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 2000,
                    }}
                >
                    <MDTypography variant="h6" sx={{ fontWeight: 700 }}>
                        Loading...
                    </MDTypography>
                </MDBox>
            ) : null}
        </MDBox>
    );
}

export default SalesDashboard;
